import React, { Fragment, useEffect, useRef, useState } from "react";
import { NavTreeNode } from "@/utils/types";
import { useDispatch, useSelector } from "react-redux";
import { useChangeLedgerStore } from "../../store/zustand/changeLedger";
import "mousetrap/plugins/global-bind/mousetrap-global-bind";
import {
  selectHoveredTempoElement,
  selectSelectedTempoElement,
  setHoveredElementKey,
  setShowContextMenu,
  setRequestedContextPosition,
  setSelectedElementKey,
  selectMultiSelectedTempoElements,
  setMultiSelectWithElementKey,
  setAllSelectedElementKeys,
  setDraggingComponent,
} from "@/store/iframeSlice";
import {
  canDragIntoNavNode,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import {
  EMPTY_TREE_CODEBASE_ID,
  STORYBOARD_TYPES,
  SKIP_ROOT_CODEBASE_ID,
} from "@/utils/constants";
import { cn } from "@/lib/utils";
import { TempoElement } from "@/utils/tempoElement";
import { AddJsxChange, MoveJsxChange } from "@/store/zustand/changeLedgerTypes";
import { ChevronDownIcon, DiamondIcon, PlayIcon } from "lucide-react";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { getLookupKeyForComponent } from "@/utils/extractElementsUtils";
import { JsxElementType } from "@/utils/jsxElementTypes";
import { createDummyComponent } from "../AppHeader/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import IconFromElementType from "./IconFromElementType";
import { getFullElementName } from "@/utils/getFullElementName";

const DomTree = React.memo(() => {
  const canvasId = useCanvasId();

  const hoveredElement: TempoElement = useSelector(selectHoveredTempoElement);
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const multiSelectedElements: TempoElement[] | null = useSelector(
    selectMultiSelectedTempoElements
  );

  const [draggingNode, setDraggingNode] = useState<NavTreeNode | null>(null);
  const [dragOverElementKey, setDragOverElementKey] = useState<string | null>(
    null
  );
  const [dragOverPosition, setDragOverPosition] = useState<
    "top" | "bottom" | "middle" | null
  >(null);

  const [previousMultiselectedElementKey, setPreviousMultiselectedElementKey] =
    useState(null);

  const [storyboardToOpen, setStoryboardToOpen] = useState<any>({});

  const dispatch = useDispatch<any>();

  const canvas = useActiveCanvas();
  const project = useActiveProject();

  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  const addChange = useChangeLedgerStore((state) => state.addChange);
  const flushing = useChangeLedgerStore((state) => state.flushing);

  const nodeOpenDict = useCanvasStore((state) => state.nodeOpenDict);
  const onClickNodeOpen = useCanvasStore((state) => state.onClickNodeOpen);

  // For auto-scrolling to element
  const [lastSelectedScrolledTo, setLastSelectedScrolledTo] = useState(null);
  const elementKeyToRef = useRef({});

  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup
  );

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );

  const { componentKeyToComponent } = useAssetComponents();

  const setCanvasComponentKey = useCanvasStore(
    (state) => state.setCanvasComponentKey
  );

  const [selectedElementFromDomTreeKey, setSelectedElementFromDomTreeKey] =
    useState(null);

  useEffect(() => {
    if (
      selectedElement.isEmpty() ||
      !storyboardToNavTree[selectedElement.storyboardId]
    ) {
      return;
    }

    if (selectedElement.getKey() === selectedElementFromDomTreeKey) {
      return;
    }

    if (selectedElement.getKey() === lastSelectedScrolledTo) {
      return;
    }

    const refToUse = elementKeyToRef.current[selectedElement.getKey()];
    if (!refToUse) {
      return;
    }

    refToUse.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    // @ts-expect-error TS2345
    setLastSelectedScrolledTo(selectedElement.getKey());
  }, [selectedElement, storyboardToNavTree]);

  useEffect(() => {
    if (!storyboardToNavTree?.[selectedElement.storyboardId]) {
      return;
    }

    const navTree = storyboardToNavTree[selectedElement.storyboardId];

    let selectedNode = null;

    const searchNode = (node: NavTreeNode) => {
      if (!node) {
        return;
      }

      if (selectedNode) {
        return;
      }

      if (node.tempoElement.isEqual(selectedElement)) {
        // @ts-expect-error TS2322
        selectedNode = node;
        return;
      }

      if (node.children) {
        node.children.forEach((child) => {
          searchNode(child);
        });
      }
    };
    searchNode(navTree);

    if (selectedNode) {
      setStoryboardToOpen({
        ...storyboardToOpen,
        [selectedElement.storyboardId]: true,
      });
    }
  }, [selectedElement]);

  useEffect(() => {
    if (
      !draggingNode ||
      dragOverPosition !== "middle" ||
      // @ts-expect-error TS2538
      nodeOpenDict[dragOverElementKey]
    ) {
      return;
    }

    const timeoutCancelId = setTimeout(() => {
      // @ts-expect-error TS2345
      onClickNodeOpen(TempoElement.fromKey(dragOverElementKey));
    }, 500);

    return () => {
      clearTimeout(timeoutCancelId);
    };
  }, [dragOverElementKey, draggingNode, dragOverPosition]);

  useEffect(() => {
    if (selectedElement.isEmpty()) {
      setPreviousMultiselectedElementKey(null);
    } else {
      // @ts-expect-error TS2345
      setPreviousMultiselectedElementKey(selectedElement.getKey());
    }
  }, [selectedElement]);

  const onClickElement = (
    tempoElement: TempoElement,
    shiftClick: boolean,
    metaClick: boolean
  ) => {
    // Shift click selects multiple, but does not deselect
    if (shiftClick && !selectedElement.isEmpty()) {
      const keyToSelectFrom =
        previousMultiselectedElementKey || tempoElement.getKey();

      const previousTempoElement = TempoElement.fromKey(keyToSelectFrom);

      // Not siblings, return
      if (!previousTempoElement.isSiblingOf(tempoElement)) {
        return;
      }

      let newMultiselectedElementKeys = multiSelectedElements?.map((elem) =>
        elem.getKey()
      ) || [selectedElement.getKey()];

      if (tempoElement.isStoryboard()) {
        // @ts-expect-error TS18047
        const orderedStoryboards = canvas.storyboards
          .filter(
            (storyboard) =>
              storyboard.deleted !== true && !storyboard.preCreated
          )
          .map((storyboard) => storyboard.id);

        const prevIndex = orderedStoryboards.indexOf(
          previousTempoElement.storyboardId
        );
        const currentIndex = orderedStoryboards.indexOf(
          tempoElement.storyboardId
        );

        for (
          let i = Math.min(prevIndex, currentIndex);
          i <= Math.max(prevIndex, currentIndex);
          i++
        ) {
          const newKeyToAdd = TempoElement.forStoryboard(
            orderedStoryboards[i]
          ).getKey();
          if (!newMultiselectedElementKeys.includes(newKeyToAdd)) {
            newMultiselectedElementKeys.push(newKeyToAdd);
          }
        }
      } else {
        const navNodeToSelectFrom = elementKeyToNavNodeLookup[keyToSelectFrom];
        const thisNavNode = elementKeyToNavNodeLookup[tempoElement.getKey()];

        const navNodeToSelectFromIndex =
          // @ts-expect-error TS18048
          navNodeToSelectFrom.parent.children.findIndex((child) =>
            child.tempoElement.isEqual(navNodeToSelectFrom.tempoElement)
          );
        // @ts-expect-error TS18048
        const thisNavNodeIndex = thisNavNode.parent.children.findIndex(
          (child) => child.tempoElement.isEqual(thisNavNode.tempoElement)
        );

        for (
          let i = Math.min(navNodeToSelectFromIndex, thisNavNodeIndex);
          i <= Math.max(navNodeToSelectFromIndex, thisNavNodeIndex);
          i++
        ) {
          const newKeyToAdd =
            // @ts-expect-error TS18048
            thisNavNode.parent.children[i].tempoElement.getKey();
          if (!newMultiselectedElementKeys.includes(newKeyToAdd)) {
            newMultiselectedElementKeys.push(newKeyToAdd);
          }
        }
      }
      dispatch(
        setAllSelectedElementKeys({
          multiSelectedElementKeys: newMultiselectedElementKeys,
          selectedElementKey: selectedElement.getKey(),
        })
      );

      // @ts-expect-error TS2345
      setPreviousMultiselectedElementKey(tempoElement.getKey());
      return;
    }

    if (metaClick) {
      dispatch(setMultiSelectWithElementKey(tempoElement.getKey()));
      // @ts-expect-error TS2345
      setPreviousMultiselectedElementKey(tempoElement.getKey());
    } else if (!selectedElement.isEqual(tempoElement)) {
      // @ts-expect-error TS2345
      setSelectedElementFromDomTreeKey(tempoElement.getKey());
      dispatch(setSelectedElementKey(tempoElement.getKey()));
      // @ts-expect-error TS2345
      setPreviousMultiselectedElementKey(tempoElement.getKey());
    }
  };

  const rowStyles = cn(
    "min-w-full flex h-8 rounded-sm font-normal flex-row items-center cursor-pointer whitespace-nowrap text-xs relative"
  );

  const getNewNavElem = (
    storyboardId: string,
    node: NavTreeNode,
    droppingInside: boolean
  ) => {
    // Note - we are only comparing storyboard IDs & unique paths here to eliminate flashing
    // when the codebase ID is updated after an API request
    const selected =
      (node.tempoElement.uniquePath === selectedElement.uniquePath &&
        node.tempoElement.storyboardId === selectedElement.storyboardId) ||
      multiSelectedElements?.find((elem) => elem.isEqual(node.tempoElement));

    // However for the hovered state we compare all values as we do not actively make sure that the unique
    // path is updated when we make an instant update, so the hovered unique path may point to a different
    // element during an update
    const hovered = node.tempoElement.isEqual(hoveredElement);

    let styleToSet = {};
    let drawOutline = false;

    let draggingOver = false;
    if (draggingNode?.tempoElement.isEqual(node.tempoElement)) {
      styleToSet["opacity"] = 1;
    } else if (dragOverElementKey === node.tempoElement.getKey()) {
      draggingOver = true;
      if (dragOverPosition === "middle") {
        drawOutline = true;
      }
    } else if (droppingInside) {
      drawOutline = true;
    }

    if (draggingNode?.scope && !selected) {
      const cannotMoveInto =
        draggingNode.tempoElement.storyboardId !== storyboardId ||
        !draggingNode?.scope ||
        draggingNode?.scope?.codebaseId !== node.scope?.codebaseId ||
        flushing;

      if (!cannotMoveInto) {
        styleToSet["background-color"] = "var(--secondary-200)";
      }
    }

    let sourceComponent;
    if (node.isComponent && treeElementLookup && componentKeyToComponent) {
      const componentInstance =
        treeElementLookup?.[node.tempoElement.codebaseId];
      if (componentInstance?.type === JsxElementType.ComponentInstance) {
        const key = getLookupKeyForComponent(componentInstance);
        // @ts-expect-error TS2538
        sourceComponent = componentKeyToComponent[key];
      }
    }

    const clickContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const position = {
        callsite: "nav",
        event: {
          clientX: e.clientX,
          clientY: e.clientY,
        },
      };

      const nodeInMultiselected = multiSelectedElements?.find((elem) =>
        elem.isEqual(node.tempoElement)
      );
      if (!nodeInMultiselected) {
        dispatch(setSelectedElementKey(node.tempoElement.getKey()));
      }

      dispatch(setShowContextMenu(true));
      dispatch(setRequestedContextPosition(position));
    };

    return (
      <div
        key={node.tempoElement.getKey()}
        // Don't allow dragging if in multiselect mode
        draggable={
          !multiSelectedElements?.length &&
          Boolean(node.tempoElement.codebaseId)
        }
        className={cn(
          rowStyles,
          selected || hovered ? "bg-background-secondary" : "",
          "select-none"
        )}
        onDoubleClick={() => {
          if (sourceComponent) {
            // @ts-expect-error TS2345
            setCanvasComponentKey(getLookupKeyForComponent(sourceComponent));
          }
        }}
        onMouseOver={() => {
          dispatch(setHoveredElementKey(node.tempoElement.getKey()));
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClickElement(node.tempoElement, e.shiftKey, e.metaKey);
        }}
        onContextMenu={clickContextMenu}
        onDragStart={(e) => {
          if (!node.tempoElement.codebaseId) {
            return;
          }

          const blankCanvas: any = document.createElement("canvas");
          e.dataTransfer?.setDragImage(blankCanvas, 0, 0);
          document.body?.appendChild(blankCanvas);
          e.dataTransfer.effectAllowed = "move";

          e.dataTransfer.setData(
            "tempo/codebaseId",
            node.tempoElement.codebaseId
          );
          e.dataTransfer.setData("tempo/dragSource", "nav");
          dispatch(setDraggingComponent("nav"));

          setDraggingNode(node);

          if (!selectedElement.isEqual(node.tempoElement)) {
            onClickElement(node.tempoElement, false, false);
          }
        }}
        onDragEnd={(e) => {
          setDraggingNode(null);
          setDragOverElementKey(null);
          dispatch(setDraggingComponent(null));
        }}
        onDragOver={(e) => {
          // Can't drop if flushing
          if (flushing) {
            return;
          }

          // Only perform this validation if it's a nav node.
          if (draggingNode) {
            // Can't drop if it's not in the same scope
            if (
              draggingNode.tempoElement.storyboardId !== storyboardId ||
              !draggingNode.scope ||
              draggingNode.scope?.codebaseId !== node.scope?.codebaseId
            ) {
              return;
            }

            // Can't drop into same element
            if (draggingNode.tempoElement.isEqual(node.tempoElement)) {
              return;
            }
          }

          // Required to allow dropping
          e.preventDefault();
          setDragOverElementKey(node.tempoElement.getKey());

          const targetHeight = e.currentTarget.getBoundingClientRect().height;
          const offsetY =
            e.clientY - e.currentTarget.getBoundingClientRect().top;

          // Bug fix Dec 14 -> can't move into an img tag, but can move around it
          if (!canDragIntoNavNode(node)) {
            if (offsetY < targetHeight / 2) {
              setDragOverPosition("top");
            } else {
              setDragOverPosition("bottom");
            }
          } else {
            if (offsetY < targetHeight / 3) {
              setDragOverPosition("top");
            } else if (
              offsetY >= targetHeight / 3 &&
              offsetY < (targetHeight / 3) * 2
            ) {
              setDragOverPosition("middle");
            } else {
              setDragOverPosition("bottom");
            }
          }
        }}
        onDragLeave={(e) => {
          if (dragOverElementKey === node.tempoElement.getKey()) {
            setDragOverElementKey(null);
          }
        }}
        onDrop={(e) => {
          // This validation is only for nav nodes
          if (draggingNode) {
            // Can't drop if it's not in the same scope
            if (
              draggingNode.tempoElement.storyboardId !== storyboardId ||
              !draggingNode.scope ||
              draggingNode.scope?.codebaseId !== node.scope?.codebaseId
            ) {
              return;
            }

            // Can't drop into same element
            if (draggingNode.tempoElement.isEqual(node.tempoElement)) {
              return;
            }

            // Dragging into parent, do nothing
            if (draggingNode.parent === node && dragOverPosition !== "top") {
              return;
            }
          }

          let staticData: any = e.dataTransfer.getData("tempo/static");
          try {
            staticData = JSON.parse(staticData);
          } catch (e) {
            staticData = null;
          }

          const codebaseId = e.dataTransfer.getData("tempo/codebaseId");

          // Currently, we support dragging nav nodes and static components.
          if (!staticData && !codebaseId) {
            return;
          }

          let codebaseIdToDropInto = null;
          let afterCodebaseId = null;
          let beforeCodebaseId = null;
          let newExpectedUniquePath = null;

          if (dragOverPosition === "middle") {
            // @ts-expect-error TS2322
            codebaseIdToDropInto = node.tempoElement.codebaseId;
            if (node.children?.length) {
              const lastChild = node.children[node.children.length - 1];
              // @ts-expect-error TS2322
              afterCodebaseId = lastChild.tempoElement.codebaseId;

              // Construct the new unique path after moving
              // @ts-expect-error TS2322
              newExpectedUniquePath =
                node.tempoElement.uniquePath + "-" + node.children.length;
            } else {
              // @ts-expect-error TS2322
              newExpectedUniquePath = node.tempoElement.uniquePath + "-0";
            }

            if (!nodeOpenDict[node.tempoElement.getKey()]) {
              onClickNodeOpen(node.tempoElement);
            }
          } else {
            // Note: for components the codebaseId is the spot where the component is used,
            // while the codebaseID is the ID of the top level div inside the component
            // @ts-expect-error TS2322
            codebaseIdToDropInto = node.parent?.tempoElement.codebaseId;

            let nodeIndexInParent = null;
            let droppingNodeIndexInParent = null;
            // @ts-expect-error TS18048
            for (let i = 0; i < node.parent.children.length; i++) {
              if (
                // @ts-expect-error TS18048
                node.parent.children[i].tempoElement.isEqual(node.tempoElement)
              ) {
                // @ts-expect-error TS2322
                nodeIndexInParent = i;
              }

              if (
                draggingNode &&
                // @ts-expect-error TS18048
                node.parent.children[i].tempoElement.isEqual(
                  draggingNode.tempoElement
                )
              ) {
                // @ts-expect-error TS2322
                droppingNodeIndexInParent = i;
              }
            }

            if (dragOverPosition === "top") {
              // No change in order, just return
              if (
                droppingNodeIndexInParent !== null &&
                droppingNodeIndexInParent === nodeIndexInParent
              ) {
                return;
              }

              const nodeAtIndex = nodeIndexInParent
                ? // @ts-expect-error TS18048
                  node.parent.children[nodeIndexInParent - 1]
                : null;

              // @ts-expect-error TS2322
              afterCodebaseId = nodeAtIndex?.tempoElement.codebaseId;

              // @ts-expect-error TS2322
              beforeCodebaseId = node?.tempoElement.codebaseId;

              // If the node being moved is before the new expected index, expect it to shift over by one
              const newExpectedIndex =
                // @ts-expect-error TS18047
                droppingNodeIndexInParent < nodeIndexInParent
                  ? // @ts-expect-error TS18047
                    nodeIndexInParent - 1
                  : nodeIndexInParent;

              // @ts-expect-error TS2322
              newExpectedUniquePath =
                // @ts-expect-error TS18048
                node.parent.tempoElement.uniquePath + "-" + newExpectedIndex;
            } else {
              // No change in order, just return
              if (
                droppingNodeIndexInParent !== null &&
                // @ts-expect-error TS18047
                droppingNodeIndexInParent === nodeIndexInParent + 1
              ) {
                return;
              }

              // @ts-expect-error TS2322
              afterCodebaseId = node.tempoElement.codebaseId;
              // @ts-expect-error TS2322
              beforeCodebaseId =
                nodeIndexInParent &&
                // @ts-expect-error TS18048
                nodeIndexInParent < node.parent.children.length - 1
                  ? // @ts-expect-error TS18048
                    node.parent.children[nodeIndexInParent + 1].tempoElement
                      .codebaseId
                  : null;

              // If the node being moved is before the new expected index, expect it to shift over by one
              const newExpectedIndex =
                // @ts-expect-error TS18047
                droppingNodeIndexInParent < nodeIndexInParent + 1
                  ? nodeIndexInParent
                  : // @ts-expect-error TS18047
                    nodeIndexInParent + 1;

              // @ts-expect-error TS2322
              newExpectedUniquePath =
                // @ts-expect-error TS18048
                node.parent.tempoElement.uniquePath + "-" + newExpectedIndex;
            }
          }

          const newExpectedTempoElement = new TempoElement(
            node.tempoElement.codebaseId,
            node.tempoElement.storyboardId,
            // @ts-expect-error TS2345
            newExpectedUniquePath
          );

          if (!codebaseIdToDropInto) {
            return;
          }

          if (codebaseId) {
            const moveJsxChange = new MoveJsxChange({
              codebaseIdToMoveTo: codebaseIdToDropInto,
              codebaseIdToMove: codebaseId,
              // @ts-expect-error TS2322
              afterCodebaseId,
              // @ts-expect-error TS2322
              beforeCodebaseId,
              expectedCurrentParentCodebaseId:
                // @ts-expect-error TS18047
                draggingNode.parent.tempoElement.codebaseId,
            });

            addChange(moveJsxChange);

            // After the instant update, update the selected element to the new expected one
            // TODO: After moving everything to zustand, call this directly in the move JSX change class
            // so it can be used elsewhere without copying
            moveJsxChange.setSelectedElementsAfterInstantUpdate(
              newExpectedTempoElement.getKey(),
              // @ts-expect-error TS2345
              null // No multiselected state
            );
          } else if (staticData) {
            const addJsxChange = new AddJsxChange({
              codebaseIdToAddTo: codebaseIdToDropInto,
              // @ts-expect-error TS2322
              beforeCodebaseId,
              // @ts-expect-error TS2322
              afterCodebaseId,
              fileContentsToSourceFrom: createDummyComponent(
                staticData.jsx,
                project.framework
              ),
              htmlForInstantUpdate: staticData.instantUpdate,
            });

            addChange(addJsxChange);
            addJsxChange.setSelectedElementsAfterInstantUpdate(
              newExpectedTempoElement.getKey(),
              // @ts-expect-error TS2345
              null
            );
          }

          setDraggingNode(null);
          setDragOverElementKey(null);
        }}
        style={styleToSet}
      >
        {drawOutline && (
          <div className="absolute top-0 left-0 w-full h-full border border-primary-200 z-[900]" />
        )}
        <div
          className={cn(
            rowStyles,
            "",
            node.isComponent ? "font-medium text-primary-n-500" : "text-white"
          )}
          draggable={false}
        >
          <div
            draggable={false}
            style={{
              flexShrink: 0,
              // @ts-expect-error TS18048
              marginLeft: `${(node.level + 1) * 1.2}rem`,
              height: "0px",
            }}
          />
          <div
            className={cn(
              "relative flex h-full w-full flex-row items-center border-transparent border-y-2",
              draggingOver &&
                dragOverPosition === "top" &&
                "border-t-primary-200",
              draggingOver &&
                dragOverPosition === "bottom" &&
                "border-b-primary-200"
            )}
          >
            {node.children?.length ? (
              <ChevronDownIcon
                className={cn(
                  "p-1 cursor-pointer max-w-none shrink text-icons-secondary w-5 h-5",
                  !nodeOpenDict[node.tempoElement.getKey()] &&
                    "rotate-[270deg]",
                  domTreeHovered ? "opacity-100" : "opacity-0"
                )}
                width={10}
                height={10}
                onClick={(e) => {
                  // A chevron click should not trigger a node click
                  e.stopPropagation();
                  onClickNodeOpen(node.tempoElement);
                }}
              />
            ) : (
              <div draggable={false} className="w-5 h-5 p-1" />
            )}

            {node.isComponent && (
              <DiamondIcon className="max-w-none w-3 h-3 mr-1" />
            )}

            <div
              draggable={false}
              className={cn("text-muted-foreground flex items-center gap-2", {
                "pl-1 text-primary-n-500": node.isComponent,
              })}
              ref={(ref) => {
                elementKeyToRef.current[node.tempoElement.getKey()] = ref;
              }}
            >
              {!node.isComponent ? (
                <IconFromElementType elementType={node.name.toLowerCase()} />
              ) : null}
              <span>
                {node.isComponent ? node.name : getFullElementName(node.name)}
              </span>
            </div>
            <div
              draggable={false}
              className="pl-2 text-text-tertiary shrink flex text-xs"
            >
              {node?.directlySetClassList?.length
                ? `${node?.directlySetClassList?.join(" ")}`
                : ""}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const displayNavTree = (
    storyboardId: string,
    node: NavTreeNode,
    show: boolean
  ): any[] => {
    let toDisplay = [];

    if (!node) {
      return toDisplay;
    }

    let droppingInside = false;
    if (
      storyboardId === draggingNode?.tempoElement.storyboardId &&
      dragOverPosition !== "middle"
    ) {
      node.children.forEach((child) => {
        if (child?.tempoElement.getKey() === dragOverElementKey) {
          droppingInside = true;
        }
      });
    }

    const isSkipRoot = node.tempoElement.codebaseId === SKIP_ROOT_CODEBASE_ID;

    // Display the node itself
    if (show && !isSkipRoot) {
      // @ts-expect-error TS2345
      toDisplay.push(getNewNavElem(storyboardId, node, droppingInside));
    }

    // The node should always be open
    const nodeOpen = nodeOpenDict[node.tempoElement.getKey()];

    // Display the children if opened
    const showChildren = (show && nodeOpen) || isSkipRoot;
    if (node.children?.length && showChildren) {
      node.children.forEach((child: any) => {
        // @ts-expect-error TS2345
        toDisplay.push(displayNavTree(storyboardId, child, showChildren));
      });
    }

    return toDisplay;
  };

  const [domTreeHovered, setDomTreeHovered] = useState(false);

  const clearContext = () => {
    dispatch(setSelectedElementKey(null));
  };

  return (
    <div
      className="w-full h-full overflow-auto select-none pt-1"
      onClick={() => clearContext()}
      onMouseLeave={() => {
        dispatch(setHoveredElementKey(null));
        setDomTreeHovered(false);
      }}
      onMouseOver={(e) => {
        if (!e.buttons && draggingNode) {
          setDraggingNode(null);
          setDragOverElementKey(null);
          dispatch(setDraggingComponent(null));
        }
      }}
      onMouseEnter={() => {
        setDomTreeHovered(true);
      }}
      onScroll={() => {
        setLastSelectedScrolledTo(null);
      }}
    >
      <div className="inline-block min-w-full">
        <div className="flex w-full flex-col">
          {canvas?.storyboards
            ?.filter(
              (storyboard: any) =>
                storyboard.deleted !== true && !storyboard.preCreated
            )
            .map((storyboard: any) => {
              let navTreeToUse = storyboardToNavTree?.[storyboard.id];
              if (!navTreeToUse) {
                navTreeToUse = {
                  tempoElement: new TempoElement(
                    EMPTY_TREE_CODEBASE_ID,
                    storyboard.id,
                    "1"
                  ),
                  children: [],
                  name: "",
                };
              }

              let name = storyboard.id;
              if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
                name = devServerUrl || "Application";
              } else {
                name = storyboard.name;
              }

              const storyboardOpen = storyboardToOpen[storyboard.id];

              const storyboardHighlighted =
                selectedElement.isStoryboard(storyboard.id) ||
                hoveredElement.isStoryboard(storyboard.id) ||
                multiSelectedElements?.find((elem) =>
                  elem.isStoryboard(storyboard.id)
                );

              const isEmptyNavTree =
                navTreeToUse?.tempoElement.codebaseId ===
                EMPTY_TREE_CODEBASE_ID;

              return (
                <Fragment key={storyboard.id}>
                  <div
                    className={cn(
                      rowStyles,
                      storyboardHighlighted && "bg-background-secondary"
                    )}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const position = {
                        callsite: "nav",
                        event: {
                          clientX: e.clientX,
                          clientY: e.clientY,
                        },
                      };

                      const tempoElement = TempoElement.forStoryboard(
                        storyboard.id
                      );

                      const nodeInMultiselected = multiSelectedElements?.find(
                        (elem) => elem.isEqual(tempoElement)
                      );
                      if (!nodeInMultiselected) {
                        dispatch(setSelectedElementKey(tempoElement.getKey()));
                      }

                      dispatch(setShowContextMenu(true));
                      dispatch(setRequestedContextPosition(position));
                    }}
                    onMouseOver={() => {
                      dispatch(
                        setHoveredElementKey(
                          TempoElement.forStoryboard(storyboard.id).getKey()
                        )
                      );
                    }}
                    onMouseOut={() => {
                      dispatch(setHoveredElementKey(null));
                    }}
                    onClick={(e) => {
                      e.stopPropagation();

                      onClickElement(
                        TempoElement.forStoryboard(storyboard.id),
                        e.shiftKey,
                        e.metaKey
                      );
                    }}
                  >
                    <ChevronDownIcon
                      className={cn(
                        "p-1 cursor-pointer max-w-none shrink text-icons-secondary h-5 w-5",
                        !storyboardOpen && "rotate-[270deg]"
                      )}
                      onClick={(e) => {
                        // A chevron click should not trigger a storyboard click
                        e.stopPropagation();
                        setStoryboardToOpen({
                          ...storyboardToOpen,
                          [storyboard.id]: !storyboardOpen,
                        });
                      }}
                      style={{
                        pointerEvents: isEmptyNavTree ? "none" : "auto",
                        opacity: isEmptyNavTree || !domTreeHovered ? 0 : 1,
                      }}
                    />
                    <PlayIcon className="h-5 w-5 bg-secondary rounded-sm text-icons-secondary p-1 mr-2" />
                    <div className="text-primary font-medium">{name}</div>
                  </div>

                  {storyboardOpen && !isEmptyNavTree
                    ? displayNavTree(storyboard.id, navTreeToUse, true)
                    : null}
                </Fragment>
              );
            })}
        </div>
      </div>
    </div>
  );
});

export default DomTree;
